import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query} from "lit/decorators.js";
import Styles from "./iButtonPopover.lit.scss";
import {FeatureDetector} from "../../common/featureDetector";
import {resolve} from "../../container";
import {KEYS, prepareEvents} from "../../common/utils/events";
import {fresh} from "../../common/lifetime";
import {PopoverAnchor} from "./popover/popoverAnchor";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";

@customElement("eop-i-button")
export class EopIButton extends PopoverAnchor {

    public static readonly styles = Styles;

    @query(".i-button")
    public iButtonElement: HTMLButtonElement;
    @property({attribute: "i-button-style"})
    public iButtonStyle: string;
    @property()
    public label: string;

    public constructor(private features: FeatureDetector = resolve(FeatureDetector)) {
        super();
    }

    public render(): TemplateResult {
        return html`
            <div class="i-button-container">
                ${this.label}
                <button
                        class="i-button ${this.iButtonStyle}"
                        aria-label="i-button"
                        ${trackingButtonInteract("i-button-toggle")}
                ></button>
            </div>
        `;
    }

    public getAnchorElement(): HTMLElement {
        return this.iButtonElement;
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        const lifetime = fresh(this, "firstUpdated");
        if (this.features.isTouchDevice()) {
            prepareEvents(this)
                .boundTo(lifetime)
                .on("click", () => {
                    this.setPopoverPosition();
                    this.popoverElement.toggle();
                });
        } else {
            prepareEvents(this)
                .boundTo(lifetime)
                .on("mouseenter", () => {
                    this.setPopoverPosition();
                    this.popoverElement.open();
                })
                .on("mouseleave", () => this.popoverElement.close());
        }

        prepareEvents(this.iButtonElement)
            .boundTo(lifetime)
            .on("keydown", (event: Event) => {
                const keyEvent = event as KeyboardEvent;
                if (keyEvent.key === KEYS.ENTER || keyEvent.key === KEYS.SPACE) {
                    this.setPopoverPosition();
                    this.popoverElement.toggle();
                } else if (keyEvent.key === KEYS.ESCAPE) {
                    this.popoverElement.close();
                    this.iButtonElement.focus();
                } else if (keyEvent.key === KEYS.TAB) {
                    this.popoverElement.close();
                }
            });

    }
}